// Admin Panel Cloud Upload Manager - Implementare completă
import React, { useState, useRef } from 'react';
import {
  Upload,
  Cloud,
  File,
  Image,
  Video,
  Archive,
  FileText,
  Music,
  X,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Pause,
  Play,
  Trash2,
  FolderOpen,
  Settings,
  Zap,
  HardDrive,
  Clock,
  Eye,
  Download
} from 'lucide-react';

const CloudUpload = () => {
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [nextId, setNextId] = useState(1);
  const [uploadSettings, setUploadSettings] = useState({
    autoStart: true,
    chunkSize: '5MB',
    maxConcurrent: 3,
    retryAttempts: 3,
    compressionEnabled: true
  });

  // Mock upload statistics
  const [stats] = useState({
    todayUploads: 47,
    totalSize: '2.3 GB',
    successRate: 98.5,
    avgSpeed: '12.4 MB/s',
    queueLength: 3,
    completedToday: 44
  });

  // File type icons and colors
  const getFileTypeInfo = (fileName) => {
    const ext = fileName.toLowerCase().split('.').pop();
    const typeMap = {
      // Images
      'jpg': { icon: Image, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20' },
      'jpeg': { icon: Image, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20' },
      'png': { icon: Image, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20' },
      'gif': { icon: Image, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20' },
      'svg': { icon: Image, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20' },
      
      // Videos
      'mp4': { icon: Video, color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20' },
      'avi': { icon: Video, color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20' },
      'mov': { icon: Video, color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20' },
      
      // Documents
      'pdf': { icon: FileText, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20' },
      'doc': { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20' },
      'docx': { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20' },
      'xls': { icon: FileText, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
      'xlsx': { icon: FileText, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
      'ppt': { icon: FileText, color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/20' },
      'pptx': { icon: FileText, color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/20' },
      
      // Archives
      'zip': { icon: Archive, color: 'text-yellow-500', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
      'rar': { icon: Archive, color: 'text-yellow-500', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
      '7z': { icon: Archive, color: 'text-yellow-500', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
      
      // Audio
      'mp3': { icon: Music, color: 'text-pink-500', bg: 'bg-pink-50 dark:bg-pink-900/20' },
      'wav': { icon: Music, color: 'text-pink-500', bg: 'bg-pink-50 dark:bg-pink-900/20' },
      'flac': { icon: Music, color: 'text-pink-500', bg: 'bg-pink-50 dark:bg-pink-900/20' }
    };
    
    return typeMap[ext] || { icon: File, color: 'text-gray-500', bg: 'bg-gray-50 dark:bg-gray-900/20' };
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUploadSpeed = (bytesPerSecond) => {
    return formatFileSize(bytesPerSecond) + '/s';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'uploading': return 'text-blue-600';
      case 'paused': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      case 'queued': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    const iconProps = { className: 'w-4 h-4' };
    
    switch (status) {
      case 'completed': return <CheckCircle2 {...iconProps} className="w-4 h-4 text-green-600" />;
      case 'uploading': return <RefreshCw {...iconProps} className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'paused': return <Pause {...iconProps} className="w-4 h-4 text-yellow-600" />;
      case 'error': return <AlertCircle {...iconProps} className="w-4 h-4 text-red-600" />;
      case 'queued': return <Clock {...iconProps} className="w-4 h-4 text-gray-600" />;
      default: return <Clock {...iconProps} className="w-4 h-4 text-gray-600" />;
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = [...e.dataTransfer.files];
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const newUploads = files.map(file => ({
      id: nextId + files.indexOf(file),
      name: file.name,
      size: file.size,
      type: file.type,
      status: uploadSettings.autoStart ? 'uploading' : 'queued',
      progress: 0,
      speed: 0,
      timeRemaining: 0,
      error: null,
      file: file
    }));

    setUploads(prev => [...prev, ...newUploads]);
    setNextId(prev => prev + files.length);

    // Simulate upload progress
    if (uploadSettings.autoStart) {
      newUploads.forEach(upload => {
        simulateUpload(upload.id);
      });
    }
  };

  const simulateUpload = (uploadId) => {
    const interval = setInterval(() => {
      setUploads(prev => prev.map(upload => {
        if (upload.id === uploadId && upload.status === 'uploading') {
          const newProgress = Math.min(upload.progress + Math.random() * 5, 100);
          const speed = Math.random() * 10000000 + 1000000; // 1-10 MB/s
          
          if (newProgress >= 100) {
            clearInterval(interval);
            return {
              ...upload,
              status: 'completed',
              progress: 100,
              speed: 0,
              timeRemaining: 0
            };
          }
          
          return {
            ...upload,
            progress: newProgress,
            speed: speed,
            timeRemaining: ((upload.size * (100 - newProgress)) / 100) / speed
          };
        }
        return upload;
      }));
    }, 500);
  };

  const pauseUpload = (uploadId) => {
    setUploads(prev => prev.map(upload =>
      upload.id === uploadId ? { ...upload, status: 'paused' } : upload
    ));
  };

  const resumeUpload = (uploadId) => {
    setUploads(prev => prev.map(upload => {
      if (upload.id === uploadId) {
        const resumed = { ...upload, status: 'uploading' };
        simulateUpload(uploadId);
        return resumed;
      }
      return upload;
    }));
  };

  const cancelUpload = (uploadId) => {
    setUploads(prev => prev.filter(upload => upload.id !== uploadId));
  };

  const clearCompleted = () => {
    setUploads(prev => prev.filter(upload => upload.status !== 'completed'));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Upload className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Upload Manager
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Încarcă și gestionează fișierele în Amazon S3
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Upload className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.todayUploads}</p>
                <p className="text-xs text-gray-500">Upload-uri azi</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <HardDrive className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSize}</p>
                <p className="text-xs text-gray-500">Volum total</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <CheckCircle2 className="w-8 h-8 text-green-500 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.successRate}%</p>
                <p className="text-xs text-gray-500">Rata de succes</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Zap className="w-8 h-8 text-yellow-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.avgSpeed}</p>
                <p className="text-xs text-gray-500">Viteză medie</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Clock className="w-8 h-8 text-orange-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.queueLength}</p>
                <p className="text-xs text-gray-500">În coadă</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <CheckCircle2 className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.completedToday}</p>
                <p className="text-xs text-gray-500">Finalizate azi</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Upload Zone */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              
              {/* Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200 ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="mb-6">
                  <Cloud className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Încarcă fișierele
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Drag & drop fișierele aici sau click pentru a selecta
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Selectează Fișiere
                  </button>
                  
                  <button
                    onClick={() => folderInputRef.current?.click()}
                    className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium rounded-lg transition-colors flex items-center"
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Selectează Folder
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFiles([...e.target.files])}
                />
                
                <input
                  ref={folderInputRef}
                  type="file"
                  webkitdirectory=""
                  className="hidden"
                  onChange={(e) => handleFiles([...e.target.files])}
                />
              </div>

              {/* Upload Queue */}
              {uploads.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Coadă de Upload ({uploads.length})
                    </h3>
                    <button
                      onClick={clearCompleted}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Șterge finalizate
                    </button>
                  </div>

                  <div className="space-y-3">
                    {uploads.map((upload) => {
                      const typeInfo = getFileTypeInfo(upload.name);
                      const Icon = typeInfo.icon;

                      return (
                        <div key={upload.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center flex-1 min-w-0">
                              <div className={`p-2 rounded-lg ${typeInfo.bg} mr-3`}>
                                <Icon className={`w-5 h-5 ${typeInfo.color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-900 dark:text-white truncate">
                                  {upload.name}
                                </h4>
                                <p className="text-sm text-gray-500">
                                  {formatFileSize(upload.size)} • {upload.status}
                                  {upload.speed > 0 && (
                                    <span> • {formatUploadSpeed(upload.speed)}</span>
                                  )}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2 ml-4">
                              {getStatusIcon(upload.status)}
                              
                              {upload.status === 'uploading' && (
                                <button
                                  onClick={() => pauseUpload(upload.id)}
                                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                >
                                  <Pause className="w-4 h-4" />
                                </button>
                              )}
                              
                              {upload.status === 'paused' && (
                                <button
                                  onClick={() => resumeUpload(upload.id)}
                                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                >
                                  <Play className="w-4 h-4" />
                                </button>
                              )}
                              
                              <button
                                onClick={() => cancelUpload(upload.id)}
                                className="p-1 text-gray-400 hover:text-red-600 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                upload.status === 'completed' ? 'bg-green-500' :
                                upload.status === 'error' ? 'bg-red-500' :
                                upload.status === 'paused' ? 'bg-yellow-500' :
                                'bg-blue-500'
                              }`}
                              style={{ width: `${upload.progress}%` }}
                            />
                          </div>

                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>{upload.progress.toFixed(1)}%</span>
                            {upload.timeRemaining > 0 && (
                              <span>~{Math.ceil(upload.timeRemaining)}s rămas</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Settings Panel */}
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center mb-6">
                <Settings className="w-5 h-5 text-gray-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Setări Upload
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadSettings.autoStart}
                      onChange={(e) => setUploadSettings(prev => ({ ...prev, autoStart: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Auto-start uploads
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uploadSettings.compressionEnabled}
                      onChange={(e) => setUploadSettings(prev => ({ ...prev, compressionEnabled: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Compresie automată
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Dimensiune chunk
                  </label>
                  <select
                    value={uploadSettings.chunkSize}
                    onChange={(e) => setUploadSettings(prev => ({ ...prev, chunkSize: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="1MB">1 MB</option>
                    <option value="5MB">5 MB</option>
                    <option value="10MB">10 MB</option>
                    <option value="25MB">25 MB</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Upload-uri simultane
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={uploadSettings.maxConcurrent}
                    onChange={(e) => setUploadSettings(prev => ({ ...prev, maxConcurrent: parseInt(e.target.value) }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1</span>
                    <span className="font-medium">{uploadSettings.maxConcurrent}</span>
                    <span>10</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Încercări reluare
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={uploadSettings.retryAttempts}
                    onChange={(e) => setUploadSettings(prev => ({ ...prev, retryAttempts: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Activitate recentă
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mr-3" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      Contract_2025.pdf
                    </p>
                    <p className="text-xs text-gray-500">Încărcat cu succes</p>
                  </div>
                </div>

                <div className="flex items-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <Upload className="w-4 h-4 text-blue-600 mr-3" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      Prezentare_Produs.pptx
                    </p>
                    <p className="text-xs text-gray-500">Se încarcă... 45%</p>
                  </div>
                </div>

                <div className="flex items-center p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <Clock className="w-4 h-4 text-gray-600 mr-3" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      Video_Marketing.mp4
                    </p>
                    <p className="text-xs text-gray-500">În coadă</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudUpload;